import { memo, useMemo, useReducer } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DetailSidebar from '../../../shared/components/DetailSidebar';
import FormActionBar from '../../../shared/components/FormActionBar';
import type { ServiceOffering, ServiceCapability } from '../../../types/backend';
import type { SubscribeArgs } from '../hooks/useSubscribedOfferings';

interface SubscribeOfferingPanelProps {
  offerings: ServiceOffering[];
  busy: boolean;
  onCancel: () => void;
  onSubscribe: (args: SubscribeArgs) => Promise<boolean>;
}

interface FormState {
  offeringId: string;
  enabled: string[];
}

type FormAction =
  | { type: 'set_offering'; offeringId: string }
  | { type: 'toggle_capability'; capabilityId: string; enabled: boolean };

function reducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'set_offering':
      return { offeringId: action.offeringId, enabled: [] };
    case 'toggle_capability':
      return {
        ...state,
        enabled: action.enabled
          ? [...state.enabled, action.capabilityId]
          : state.enabled.filter((id) => id !== action.capabilityId),
      };
  }
}

function pinnedVersion(offering: ServiceOffering): string {
  return offering.manifest?.version ?? offering.version ?? '';
}

function offeringCapabilities(offering: ServiceOffering): ServiceCapability[] {
  return offering.manifest?.capabilities ?? offering.capabilities ?? [];
}

const SubscribeOfferingPanel = memo<SubscribeOfferingPanelProps>(({
  offerings,
  busy,
  onCancel,
  onSubscribe,
}) => {
  const [form, dispatch] = useReducer(reducer, { offeringId: '', enabled: [] });

  const selectedOffering = useMemo(
    () => offerings.find((o) => o.id === form.offeringId) ?? null,
    [offerings, form.offeringId],
  );

  const capabilities = selectedOffering ? offeringCapabilities(selectedOffering) : [];
  const version = selectedOffering ? pinnedVersion(selectedOffering) : '';
  const canSubmit = !busy && Boolean(selectedOffering) && version !== '';

  const handleSubmit = async () => {
    if (!selectedOffering || version === '') return;
    const ok = await onSubscribe({
      offeringId: selectedOffering.id,
      pinnedOfferingVersion: version,
      enabledCapabilities: form.enabled,
    });
    if (ok) onCancel();
  };

  return (
    <DetailSidebar title="Subscribe to an offering" onClose={onCancel}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {offerings.length === 0 ? (
            <Alert severity="info">
              No published offerings are available to subscribe to.
            </Alert>
          ) : (
            <TextField
              select
              size="small"
              fullWidth
              label="Offering"
              value={form.offeringId}
              onChange={(e) => dispatch({ type: 'set_offering', offeringId: e.target.value })}
              helperText="A published offering to subscribe this customer to."
            >
              {offerings.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2">{o.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {pinnedVersion(o) || '—'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}

          {selectedOffering && version === '' && (
            <Alert severity="warning">
              This offering has no published version and cannot be subscribed to.
            </Alert>
          )}

          {selectedOffering && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.25 }}>Capabilities</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Select which capabilities to enable. None are enabled by default.
              </Typography>
              {capabilities.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This offering defines no capabilities.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {capabilities.map((cap) => (
                    <FormControlLabel
                      key={cap.capability_id}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.enabled.includes(cap.capability_id)}
                          onChange={(e) =>
                            dispatch({ type: 'toggle_capability', capabilityId: cap.capability_id, enabled: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{cap.display_name}</Typography>
                          {cap.description && (
                            <Typography variant="caption" color="text.secondary">{cap.description}</Typography>
                          )}
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mx: 0, py: 0.25 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <FormActionBar
            onSave={handleSubmit}
            saving={busy}
            saveDisabled={!canSubmit}
            saveLabel="Subscribe"
            savingLabel="Subscribing..."
            sx={{ mt: 0, pt: 0, borderTop: 0 }}
          >
            <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          </FormActionBar>
        </Box>
      </Box>
    </DetailSidebar>
  );
});

SubscribeOfferingPanel.displayName = 'SubscribeOfferingPanel';
export default SubscribeOfferingPanel;
